import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ReauthenticationBoundary } from '@/components/workspace/ReauthenticationBoundary';
import { TrainingBanner } from '@/features/training/TrainingBanner';
import { ExercisePanel } from '@/features/training/ExercisePanel';
import { ExerciseRuntime } from '@/features/training/ExerciseRuntime';
import { TrainingOverlay } from '@/features/training/TrainingOverlay';
import { TrainingPracticeRecorder } from '@/features/training/TrainingPracticeRecorder';
import { TrainingTourRuntime } from '@/features/training/TrainingTourRuntime';
import { useFeatures } from '@/hooks/useFeatures';
import { RouteErrorScreen } from '@/router/RouteErrorScreen';
import { useRouteChangeEffects } from '@/router/useRouteChangeEffects';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

/**
 * Каркас рабочего места по макету-эталону docs/design/frontend-v2: ссылка «К содержимому»,
 * боковая навигация, шапка с хлебными крошками, содержимое страницы и подвал -- на каждом
 * экране, и на карте (index.html:99-102). Повторный вход открывается поверх страницы, не
 * размонтируя её; падение страницы показывает экран аварии на её месте, каркас остаётся.
 * Серверные флаги функций загружает каркас: по ним навигация показывает разделы,
 * а страницы за флагом ждут ответа.
 */
export function AppLayout() {
	const { t } = useTranslation('common');
	const location = useLocation();
	const mainRef = useRef<HTMLElement>(null);
	useFeatures();
	useRouteChangeEffects(mainRef);

	return (
		<ReauthenticationBoundary>
			<a
				className="ws-skip-link"
				href="#main"
				onClick={(event) => {
					event.preventDefault();
					mainRef.current?.focus();
				}}
			>
				{t('shell.skipToContent')}
			</a>
			<Sidebar />
			<div className="ws-app">
				<Topbar />
				<TrainingBanner />
				<ExercisePanel />
				<ExerciseRuntime />
				<TrainingPracticeRecorder />
				<TrainingTourRuntime />
				<TrainingOverlay />
				<main id="main" ref={mainRef} tabIndex={-1} className="ws-main">
					<ErrorBoundary
						resetKey={location.key}
						fallback={(retry) => <RouteErrorScreen onRetry={retry} />}
					>
						<Outlet />
					</ErrorBoundary>
				</main>
				<footer className="ws-app-footer">
					<span>{t('shell.product')}</span>
				</footer>
			</div>
		</ReauthenticationBoundary>
	);
}
